import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../context/ModalContext';
import {
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  importMembers,
  getImportHistory,
  downloadTemplate,
  clearApiMessage,
  clearError,
  selectMembersLoading,
  selectMembersApiMessage,
  selectMembersError,
  selectImportHistory,
  selectImportHistoryPagination,
  selectImportResult,
} from '../../store/slices/main/memberSlice';
import { Lightbulb, Download, BarChart3, ClipboardList, TrendingUp, FileText, FolderOpen, Trash2, AlertTriangle, CheckCircle2, Inbox } from 'lucide-react';

const BulkImport = ({ showToast }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [duplicateHandling, setDuplicateHandling] = useState('skip');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [validationResult, setValidationResult] = useState(null);
  const [isDryRun, setIsDryRun] = useState(true);
  const [importStats, setImportStats] = useState(null);
  
  const fileInputRef = useRef(null);
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const loading = useSelector(selectMembersLoading);
  const apiMessage = useSelector(selectMembersApiMessage);
  const error = useSelector(selectMembersError);
  const importHistory = useSelector(selectImportHistory) || [];
  const importHistoryPagination = useSelector(selectImportHistoryPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const importResult = useSelector(selectImportResult);

  // ==================== HELPER: FORMAT DATE ====================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // ==================== HELPER: FORMAT PHONE ====================
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return 'N/A';
    let cleaned = String(phone).replace(/[^0-9+]/g, '');
    if (cleaned.match(/^[0-9]+\.[0-9]+E\+[0-9]+$/i)) {
      cleaned = Number(cleaned).toString();
    }
    if (cleaned.startsWith('233') && !cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  // ==================== EFFECTS ====================
  
  // Load import history on mount and when pagination changes - SAME AS MEMBERS
  useEffect(() => {
    dispatch(getImportHistory({ 
      per_page: itemsPerPage, 
      page: currentPage 
    }));
  }, [dispatch, currentPage, itemsPerPage]);

  // Display API messages
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // Update preview when import result changes
  useEffect(() => {
    if (importResult && importResult.data) {
      const result = importResult.data;
      
      if (result.dry_run) {
        const validData = result.valid_data || [];
        const invalidData = result.invalid_data || [];
        
        const previewRows = [
          ...validData.map((item, index) => ({
            id: `valid-${index}`,
            row: item.row,
            name: `${item.data.first_name} ${item.data.last_name}`,
            phone: formatPhoneForDisplay(item.data.phone),
            group: item.data.group,
            status: 'valid'
          })),
          ...invalidData.map((item, index) => ({
            id: `invalid-${index}`,
            row: item.row,
            name: `${item.data.first_name} ${item.data.last_name}`,
            phone: formatPhoneForDisplay(item.data.phone),
            group: item.data.group,
            status: 'warning',
            errors: item.errors
          }))
        ];
        
        setPreviewData(previewRows);
        setValidationResult(result);
        setIsDryRun(true);
        setImportStats(null);
      } else {
        const summary = result.summary || {};
        setImportStats(summary);
        
        setIsImporting(false);
        setImportProgress(100);
        setFile(null);
        setPreviewData([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh history - go to first page
        setCurrentPage(1);
        dispatch(getImportHistory({ 
          per_page: itemsPerPage, 
          page: 1 
        }));
      }
    }
  }, [importResult, dispatch, itemsPerPage]);

  // ==================== HANDLERS ====================

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFileUpload(file);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file) => {
    const validExt = ['csv'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    
    if (!validExt.includes(fileExt)) {
      showToastMessage('⚠️ Please upload a valid CSV file', 'error');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToastMessage('⚠️ File size exceeds 10MB limit', 'error');
      return;
    }

    setFile(file);
    showToastMessage(`📄 File loaded: ${file.name}`, 'success');
    
    handleValidateFile(file);
  };

  const handleValidateFile = async (file) => {
    setIsDryRun(true);
    setPreviewData([]);
    setImportStats(null);
    
    try {
      const result = await dispatch(importMembers({ 
        file, 
        duplicate_handling: duplicateHandling,
        dry_run: true
      })).unwrap();
      
      if (result.success && result.data) {
        const data = result.data;
        const validData = data.valid_data || [];
        const invalidData = data.invalid_data || [];
        
        const previewRows = [
          ...validData.map((item, index) => ({
            id: `valid-${index}`,
            row: item.row,
            name: `${item.data.first_name} ${item.data.last_name}`,
            phone: formatPhoneForDisplay(item.data.phone),
            group: item.data.group,
            status: 'valid'
          })),
          ...invalidData.map((item, index) => ({
            id: `invalid-${index}`,
            row: item.row,
            name: `${item.data.first_name} ${item.data.last_name}`,
            phone: formatPhoneForDisplay(item.data.phone),
            group: item.data.group,
            status: 'warning',
            errors: item.errors
          }))
        ];
        
        setPreviewData(previewRows);
        setValidationResult(data.summary);
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to validate file', 'error');
    }
  };

  const downloadTemplateFile = async () => {
    try {
      await dispatch(downloadTemplate('csv')).unwrap();
      showToastMessage('📥 Template downloaded successfully!', 'success');
    } catch (err) {
      showToastMessage(err.message || 'Failed to download template', 'error');
    }
  };

  const getValidationSummary = () => {
    const total = previewData.length;
    const valid = previewData.filter(d => d.status === 'valid').length;
    const warnings = previewData.filter(d => d.status === 'warning').length;
    return { total, valid, warnings };
  };

  const handleImport = async () => {
    if (!file) {
      showToastMessage('⚠️ Please select a file to import', 'error');
      return;
    }

    const { total, valid, warnings } = getValidationSummary();
    if (valid === 0) {
      showToastMessage('⚠️ No valid data to import', 'error');
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    setImportStats(null);

    try {
      const result = await dispatch(importMembers({
        file,
        duplicate_handling: duplicateHandling,
        dry_run: false
      })).unwrap();

      if (result.success) {
        const data = result.data;
        const summary = data.summary || {};
        
        showToastMessage(`✅ ${summary.imported || 0} members imported successfully!`, 'success');
      }
    } catch (err) {
      showToastMessage(err.message || 'Failed to import members', 'error');
      setIsImporting(false);
      setImportProgress(0);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreviewData([]);
    setValidationResult(null);
    setImportProgress(0);
    setImportStats(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    showToastMessage('🗑️ File cleared', 'info');
  };

  // ==================== EXPORT FUNCTIONS ====================

  const openExportModal = (exportType = 'all', historyItem = null) => {
    let data = [];
    let title = 'Import History Report';
    let filename = 'import-history';

    if (exportType === 'single' && historyItem) {
      data = [{
        'Date': formatDate(historyItem.created_at),
        'File': historyItem.filename,
        'Imported': historyItem.imported || 0,
        'Skipped': historyItem.skipped || 0,
        'Failed': historyItem.failed || 0,
        'Status': historyItem.status
      }];
      title = `Import Record: ${historyItem.filename}`;
      filename = `import-${historyItem.id}`;
    } else if (exportType === 'preview') {
      const validData = previewData.filter(d => d.status === 'valid');
      if (validData.length === 0) {
        showToastMessage('No preview data to export', 'error');
        return;
      }
      data = validData.map(d => ({
        'Name': d.name,
        'Phone': d.phone,
        'Group': d.group,
        'Status': 'Valid'
      }));
      title = 'Import Preview Data';
      filename = 'import-preview';
    } else if (exportType === 'summary') {
      const totalImported = importHistory.reduce((sum, h) => sum + (h.imported || 0), 0);
      const totalSkipped = importHistory.reduce((sum, h) => sum + (h.skipped || 0), 0);
      const totalFailed = importHistory.reduce((sum, h) => sum + (h.failed || 0), 0);
      
      data = [
        { 'Metric': 'Total Imports', 'Value': importHistory.length },
        { 'Metric': 'Total Members Imported', 'Value': totalImported },
        { 'Metric': 'Total Skipped', 'Value': totalSkipped },
        { 'Metric': 'Total Failed', 'Value': totalFailed },
        { 'Metric': 'Avg Import Per File', 'Value': importHistory.length > 0 ? Math.round(totalImported / importHistory.length) : 0 },
        { 'Metric': 'Success Rate', 'Value': (totalImported + totalFailed) > 0 ? `${((totalImported / (totalImported + totalFailed)) * 100).toFixed(1)}%` : '0%' }
      ];
      title = 'Import Summary Report';
      filename = 'import-summary';
    } else {
      data = importHistory.map(h => ({
        'Date': formatDate(h.created_at),
        'File': h.filename,
        'Imported': h.imported || 0,
        'Skipped': h.skipped || 0,
        'Failed': h.failed || 0,
        'Status': h.status
      }));
      if (data.length === 0) {
        showToastMessage('No import history to export', 'error');
        return;
      }
      title = 'Import History Report';
      filename = 'all-imports';
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    switch (format) {
                      case 'CSV':
                        exportToCSV(data, getExportFilename(filename));
                        break;
                      case 'JSON':
                        exportToJSON(data, getExportFilename(filename));
                        break;
                      case 'Excel':
                        exportToExcel(data, getExportFilename(filename));
                        break;
                      case 'PDF':
                        exportToPDF(data, title, getExportFilename(filename));
                        break;
                      default:
                        showToastMessage('Export format not supported', 'error');
                        return;
                    }
                    showToastMessage(`📥 ${data.length} records exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> {data.length} records will be exported</span>
            <span style={{ fontSize: '11px' }}>{title}</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  const handleExportAll = () => {
    if (importHistory.length === 0) {
      showToastMessage('No import history to export', 'error');
      return;
    }
    openExportModal('all');
  };

  const handleExportImport = (historyItem) => {
    openExportModal('single', historyItem);
  };

  const handleExportPreview = () => {
    const validData = previewData.filter(d => d.status === 'valid');
    if (validData.length === 0) {
      showToastMessage('No preview data to export', 'error');
      return;
    }
    openExportModal('preview');
  };

  const handleExportSummary = () => {
    if (importHistory.length === 0) {
      showToastMessage('No import history to export', 'error');
      return;
    }
    openExportModal('summary');
  };

  // ==================== PAGINATION - SAME AS MEMBERS ====================

  const totalItems = importHistoryPagination.total || 0;
  const totalPages = importHistoryPagination.last_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== GO BACK TO MEMBERS ====================
  const handleGoBack = () => {
    navigate('/members');
  };

  // ==================== SHOW TOAST ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== RENDER ====================

  const { total, valid, warnings } = getValidationSummary();
  const importReady = file && previewData.length > 0 && valid > 0;

  return (
    <>
      {/* Back Button */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <button 
          className="btn btn-ghost btn-sm" 
          onClick={handleGoBack}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '8px 16px'
          }}
        >
          ← Back to Members
        </button>
        
        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)' }}>
          <Download size={16} style={{ verticalAlign: '-3px' }} /> Bulk Import Members
        </div>
        
        <div style={{ width: '120px' }}></div>
      </div>

      <div style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <span>Upload a CSV file to add multiple members at once.</span>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {file && previewData.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={handleExportPreview}>
              <Download size={16} style={{ verticalAlign: '-3px' }} /> Export Preview
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={handleExportSummary}>
            <BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Summary
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleExportAll}>
            <Download size={16} style={{ verticalAlign: '-3px' }} /> Export History
          </button>
          {file && (
            <button className="btn btn-ghost btn-sm" onClick={clearFile}>
              <Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Clear File
            </button>
          )}
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom: '14px' }}>Upload Member CSV</div>
          <div
            style={{ 
              border: `2px dashed ${dragActive ? 'var(--gold)' : 'var(--border)'}`,
              borderRadius: '12px', 
              padding: '40px 24px', 
              textAlign: 'center', 
              cursor: 'pointer', 
              transition: 'all .2s',
              marginBottom: '16px',
              background: dragActive ? 'var(--gold-pale)' : 'transparent'
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            {file ? (
              <>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}><FileText size={40} strokeWidth={1.5} /></div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginBottom: '4px' }}>
                  {file.name}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                  {(file.size / 1024).toFixed(1)} KB · Ready to import
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}><FolderOpen size={40} strokeWidth={1.5} /></div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginBottom: '4px' }}>
                  Drop your CSV here
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                  or click to browse · .CSV only
                </div>
              </>
            )}
          </div>
          
          <button 
            className="btn btn-ghost btn-sm" 
            style={{ width: '100%', marginBottom: '12px' }} 
            onClick={downloadTemplateFile}
          >
            ⬇ Download CSV Template
          </button>
          
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)', marginBottom: '6px' }}>
              Required Columns:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {['first_name', 'last_name', 'phone', 'dob', 'gender', 'group', 'status', 'branch', 'email'].map(c => (
                <span key={c} style={{ 
                  fontFamily: 'JetBrains Mono, monospace', 
                  fontSize: '11px', 
                  padding: '2px 7px', 
                  background: 'var(--navy)', 
                  color: 'var(--gold-light)', 
                  borderRadius: '4px' 
                }}>
                  {c}
                </span>
              ))}
            </div>
          </div>
          
          <div style={{ background: 'var(--amber-bg)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: 'var(--amber)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> Phone numbers must be in international format: <strong>+233244123456</strong>
          </div>

          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '10px 12px', fontSize: '11px', color: 'var(--text-light)', marginTop: '8px' }}>
            <Lightbulb size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> Tip: Use the template above to ensure correct formatting.
          </div>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: '14px' }}>
            Import Preview
            {file && (
              <span style={{ fontSize: '12px', fontWeight: '400', color: 'var(--text-light)', marginLeft: '8px' }}>
                · {total} rows detected
              </span>
            )}
          </div>
          
          {file && (
            <div style={{ 
              background: warnings > 0 ? 'var(--amber-bg)' : 'var(--green-bg)', 
              borderRadius: '8px', 
              padding: '10px 12px', 
              marginBottom: '14px', 
              fontSize: '13px', 
              color: warnings > 0 ? 'var(--amber)' : 'var(--green)'
            }}>
              {warnings > 0 ? (
                <span dangerouslySetInnerHTML={{ __html: `⚠️ <strong>${total} rows detected</strong> · ${warnings} warning${warnings > 1 ? 's' : ''} · Review before importing` }} />
              ) : (
                <span dangerouslySetInnerHTML={{ __html: `✅ <strong>${total} rows detected</strong> · Ready to import` }} />
              )}
            </div>
          )}

          <div className="table-wrap" style={{ marginBottom: '14px' }}>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Group</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {previewData.length > 0 ? (
                  previewData.map((row, index) => (
                    <tr key={row.id} style={{ opacity: row.status === 'warning' ? 0.8 : 1 }}>
                      <td style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                        {row.row || index + 1}
                      </td>
                      <td style={{ fontSize: '12px', fontWeight: '500' }}>{row.name}</td>
                      <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' }}>
                        {row.phone}
                      </td>
                      <td style={{ fontSize: '12px' }}>{row.group}</td>
                      <td>
                        {row.status === 'valid' && <span style={{ color: 'var(--green)' }}><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Valid</span>}
                        {row.status === 'warning' && <span style={{ color: 'var(--amber)' }}><AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> {row.errors ? Object.values(row.errors).flat().join(', ') : 'Warning'}</span>}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-light)' }}>
                      {loading ? 'Loading...' : 'Upload a file to preview data'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {isImporting && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-mid)', marginBottom: '4px' }}>
                <span>Importing... {importProgress}%</span>
                <span>{Math.floor(importProgress / 100 * total)} of {total}</span>
              </div>
              <div className="progress-bar" style={{ height: '8px' }}>
                <div className="progress-fill" style={{ width: `${importProgress}%`, background: 'var(--gold)' }} />
              </div>
            </div>
          )}

          {importStats && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '8px',
              marginBottom: '14px',
              padding: '10px',
              background: 'var(--cream)',
              borderRadius: '8px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--green)' }}>
                  {importStats.imported || 0}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Imported</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--amber)' }}>
                  {importStats.skipped || 0}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Skipped</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--red)' }}>
                  {importStats.failed || 0}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Failed</div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Duplicate Handling</label>
            <select 
              className="form-select"
              value={duplicateHandling}
              onChange={(e) => {
                setDuplicateHandling(e.target.value);
                if (file) {
                  handleValidateFile(file);
                }
              }}
              disabled={isImporting}
            >
              <option value="skip">Skip duplicates</option>
              <option value="update">Update existing</option>
              <option value="add">Add as new</option>
            </select>
          </div>

          <button 
            className="btn btn-gold btn-sm" 
            style={{ width: '100%' }}
            onClick={handleImport}
            disabled={!importReady || isImporting || loading}
          >
            {isImporting ? '⏳ Importing...' : loading ? '⏳ Processing...' : (<><Download size={16} style={{ verticalAlign: '-3px' }} /> Import {valid} Members</>)}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title" style={{ marginBottom: '0' }}>Import History</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              className="btn btn-ghost btn-sm"
              onClick={handleExportSummary}
              title="Export summary"
            >
              <BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Summary
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleExportAll}
              title="Export all history"
            >
              <Download size={16} style={{ verticalAlign: '-3px' }} /> Export All
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>File</th>
                <th>Imported</th>
                <th>Skipped</th>
                <th>Failed</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-light)' }}>
                    <div>Loading history...</div>
                  </td>
                </tr>
              ) : importHistory.length > 0 ? (
                importHistory.map((item) => (
                  <tr key={item.id}>
                    <td style={{ fontSize: '12px' }}>
                      {formatDate(item.created_at)}
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: 'var(--blue)' }}>
                      {item.filename}
                    </td>
                    <td style={{ fontWeight: '600', color: 'var(--green)' }}>{item.imported || 0}</td>
                    <td style={{ color: 'var(--amber)' }}>{item.skipped || 0}</td>
                    <td style={{ color: 'var(--red)' }}>{item.failed || 0}</td>
                    <td>
                      <span className={`badge ${
                        item.status === 'completed' ? 'badge-green' : 
                        item.status === 'failed' ? 'badge-red' : 
                        item.status === 'completed_with_errors' ? 'badge-amber' :
                        'badge-amber'
                      }`}>
                        {item.status === 'completed' ? 'Completed' : 
                         item.status === 'failed' ? 'Failed' : 
                         item.status === 'completed_with_errors' ? 'With Errors' :
                         'No Changes'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button 
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => handleExportImport(item)}
                          title={`Export ${item.filename}`}
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-light)' }}>
                    <div style={{ fontSize: '40px', marginBottom: '8px' }}><Inbox size={40} strokeWidth={1.5} /></div>
                    <div>No import history</div>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      Import your first CSV file to get started
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - EXACTLY THE SAME AS MEMBERS */}
        {totalItems > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            padding: '14px 0 0',
            borderTop: '1px solid var(--border)',
            marginTop: '4px'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPage * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> records
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select 
                  className="form-select" 
                  style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  title="Items per page"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', gap: '4px' }}>
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  style={{ 
                    opacity: currentPage === 1 ? 0.5 : 1, 
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer' 
                  }}
                  title="Previous page"
                >
                  ← Prev
                </button>
                
                {getPageNumbers().map(page => (
                  <button 
                    key={page}
                    className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => goToPage(page)}
                    title={`Go to page ${page}`}
                  >
                    {page}
                  </button>
                ))}
                
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  style={{ 
                    opacity: currentPage === totalPages ? 0.5 : 1, 
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' 
                  }}
                  title="Next page"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-red {
          background: var(--red-bg);
          color: var(--red);
        }
        .btn-primary {
          background: var(--navy) !important;
          color: white !important;
          border: none !important;
        }
        .btn-primary:hover {
          background: var(--navy-dark) !important;
          color: white !important;
        }
        .btn-icon {
          padding: 4px 6px !important;
          min-width: 28px;
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
          background: var(--cream);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .progress-bar {
          width: 100%;
          height: 8px;
          background: var(--border);
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
        }
        @media (max-width: 480px) {
          .table-wrap {
            overflow-x: auto;
          }
          .table-wrap table {
            min-width: 700px;
          }
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
      `}</style>
    </>
  );
};

export default BulkImport;